import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  WebflowOAuthStartResponseDto,
  WebflowProjectStatusResponseDto,
  WebflowSiteDto,
  WebflowSitesResponseDto,
  WebflowPagesResponseDto,
  WebflowPageDto,
  WebflowMessageResponseDto,
  WebflowActivityLogsResponseDto,
  WebflowSyncStatusResponseDto,
  WebflowSyncSitePagesResponseDto,
  ActivityLogDto,
  ActivityLogsParamsDto,
} from '../models/webflow.dto';
import { SitePageDto } from '../models/request.dto';

@Injectable({
  providedIn: 'root'
})
export class WebflowService {
  private readonly apiUrl = `${environment.apiHost}/api`;

  constructor(private readonly http: HttpClient) {}

  public getOAuthStartUrl(organizationId: string, projectId?: string): Observable<WebflowOAuthStartResponseDto> {
    const params: Record<string, string> = { organization_id: organizationId };
    if (projectId) {
      params['project_id'] = projectId;
    }
    return this.http.get<WebflowOAuthStartResponseDto>(
      `${this.apiUrl}/webflow/oauth/start/`,
      { params }
    );
  }

  public getProjectStatus(organizationId: string, projectId: string): Observable<WebflowProjectStatusResponseDto> {
    return this.http.get<WebflowProjectStatusResponseDto>(
      `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/status/`
    );
  }

  public disconnectProject(organizationId: string, projectId: string): Observable<WebflowMessageResponseDto> {
    return this.http.delete<WebflowMessageResponseDto>(
      `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/disconnect/`
    );
  }

  public getSyncStatus(organizationId: string, projectId: string): Observable<WebflowSyncStatusResponseDto> {
    return this.http.get<WebflowSyncStatusResponseDto>(
      `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/sync-status/`
    );
  }

  public getProjectSites(organizationId: string, projectId: string, forceRefresh = false): Observable<WebflowSiteDto[]> {
    const url = `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/sites/`;
    const request$ = forceRefresh
      ? this.http.get<WebflowSitesResponseDto>(url, { params: { refresh: 'true' } })
      : this.http.get<WebflowSitesResponseDto>(url);
    return request$.pipe(map(res => res.data.sites));
  }

  public getProjectPages(organizationId: string, projectId: string, siteId: string): Observable<WebflowPageDto[]> {
    return this.http
      .get<WebflowPagesResponseDto>(
        `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/sites/${siteId}/pages/`
      )
      .pipe(map(res => res.data.pages));
  }

  public syncSitePages(organizationId: string, projectId: string, webflowSiteId: string): Observable<SitePageDto[]> {
    return this.http
      .post<WebflowSyncSitePagesResponseDto>(
        `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/sites/${webflowSiteId}/sync-pages/`,
        {}
      )
      .pipe(map(res => res.data.pages));
  }

  public getActivityLogs(
    organizationId: string,
    projectId: string,
    webflowSiteId: string,
    params: ActivityLogsParamsDto = {}
  ): Observable<ActivityLogDto[]> {
    let httpParams = new HttpParams();
    if (params.limit !== undefined) httpParams = httpParams.set('limit', params.limit);
    if (params.event_type) httpParams = httpParams.set('event_type', params.event_type);
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);

    return this.http
      .get<WebflowActivityLogsResponseDto>(
        `${this.apiUrl}/webflow/${organizationId}/projects/${projectId}/sites/${webflowSiteId}/activity-logs/`,
        { params: httpParams }
      )
      .pipe(map(res => res.data.activity_logs));
  }
}
